// frontend/src/pages/Encuesta/EncuestaPublicaPage.js
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import encuestaService from '../../services/encuestaService';
import {
    CLAVE_RESPONDIDA,
    COLOR_PRIMARIO,
    COLOR_SECUNDARIO,
    LARGO_MAXIMO_TEXTO,
    MENSAJE_CIERRE,
    NO_APLICA,
    PREGUNTAS,
    PREGUNTAS_OBLIGATORIAS,
    TEXTO_INTRODUCCION,
    TITULO_ENCUESTA,
    construirPayload,
} from './definicionEncuesta';

/**
 * Formulario público de la encuesta de satisfacción (/encuesta).
 *
 * Vive FUERA de ProtectedRoute: se abre desde un link de WhatsApp, sin login y casi siempre desde el
 * celular. Por eso está pensada mobile-first (una columna, botones grandes de tocar) y con estilos
 * en línea como el resto de las pantallas públicas, sin depender de nada del área privada.
 *
 * Todas las preguntas van en una sola página: son diez y se responden en tres minutos; paginarlas
 * sólo sumaría pasos donde la gente abandona.
 */
const EncuestaPublicaPage = () => {
    const [respuestas, setRespuestas] = useState({});
    const [faltantes, setFaltantes] = useState([]);
    const [enviando, setEnviando] = useState(false);
    const [error, setError] = useState(null);
    const [enviado, setEnviado] = useState(false);

    // Anti-spam del lado del navegador: si ya respondió, ve el agradecimiento en vez del formulario.
    // Va en try/catch porque en modo incógnito o con el almacenamiento bloqueado leer localStorage
    // tira excepción, y eso no puede dejar la encuesta en blanco.
    useEffect(() => {
        try {
            if (window.localStorage.getItem(CLAVE_RESPONDIDA)) {
                setEnviado(true);
            }
        } catch (e) {
            // Sin localStorage disponible simplemente mostramos el formulario.
        }
    }, []);

    const responder = useCallback((codigo, valor) => {
        setRespuestas((previas) => ({ ...previas, [codigo]: valor }));
        setFaltantes((previas) => previas.filter((c) => c !== codigo));
    }, []);

    const codigosFaltantes = useMemo(
        () => PREGUNTAS_OBLIGATORIAS
            .filter((p) => {
                const valor = respuestas[p.codigo];
                return valor === undefined || valor === null || valor === '';
            })
            .map((p) => p.codigo),
        [respuestas]
    );

    const enviar = async (evento) => {
        evento.preventDefault();
        setError(null);

        if (codigosFaltantes.length > 0) {
            setFaltantes(codigosFaltantes);
            const primera = document.getElementById(`pregunta-${codigosFaltantes[0]}`);
            if (primera) primera.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return;
        }

        setEnviando(true);
        try {
            await encuestaService.enviarRespuesta(construirPayload(respuestas));
            try {
                window.localStorage.setItem(CLAVE_RESPONDIDA, new Date().toISOString());
            } catch (e) {
                // Si no se puede marcar, la respuesta igual se guardó: no es motivo para mostrar error.
            }
            setEnviado(true);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } catch (e) {
            // El backend manda el motivo en ApiError.message (429 por límite, 400 por dato inválido).
            setError(e?.response?.data?.message
                || 'No pudimos guardar tu respuesta. Revisá tu conexión y probá de nuevo.');
        } finally {
            setEnviando(false);
        }
    };

    if (enviado) {
        return (
            <Pantalla>
                <div style={estilos.tarjetaCierre}>
                    <div style={estilos.iconoCierre}>
                        <i className="bi bi-check-lg" />
                    </div>
                    <h2 style={estilos.tituloCierre}>{MENSAJE_CIERRE}</h2>
                </div>
            </Pantalla>
        );
    }

    return (
        <Pantalla>
            <form onSubmit={enviar} noValidate>
                {PREGUNTAS.map((pregunta) => (
                    <Pregunta
                        key={pregunta.codigo}
                        pregunta={pregunta}
                        valor={respuestas[pregunta.codigo]}
                        falta={faltantes.includes(pregunta.codigo)}
                        onResponder={responder}
                    />
                ))}

                {error && (
                    <div style={estilos.error} role="alert">
                        <i className="bi bi-exclamation-triangle-fill" style={{ marginRight: '0.5rem' }} />
                        {error}
                    </div>
                )}

                {faltantes.length > 0 && (
                    <div style={estilos.error} role="alert">
                        <i className="bi bi-exclamation-triangle-fill" style={{ marginRight: '0.5rem' }} />
                        Te faltan {faltantes.length === 1 ? 'una pregunta' : `${faltantes.length} preguntas`} por responder.
                    </div>
                )}

                <button type="submit" disabled={enviando} style={estilos.botonEnviar(enviando)}>
                    {enviando ? 'Enviando...' : 'Enviar respuestas'}
                </button>

                <p style={estilos.pieAnonimo}>
                    <i className="bi bi-shield-check" style={{ marginRight: '0.4rem' }} />
                    No guardamos tu nombre, tu usuario ni nada que permita saber quién respondió.
                </p>
            </form>
        </Pantalla>
    );
};

/** Encabezado y contenedor centrado, compartidos por el formulario y el agradecimiento. */
const Pantalla = ({ children }) => (
    <div style={estilos.fondo}>
        <header style={estilos.encabezado}>
            <div style={estilos.contenedor}>
                <h1 style={estilos.titulo}>{TITULO_ENCUESTA}</h1>
                <p style={estilos.introduccion}>{TEXTO_INTRODUCCION}</p>
            </div>
        </header>
        <main style={{ ...estilos.contenedor, paddingBottom: '3rem' }}>{children}</main>
    </div>
);

/** Una pregunta con su enunciado y el control que corresponda a su tipo. */
const Pregunta = ({ pregunta, valor, falta, onResponder }) => (
    <section id={`pregunta-${pregunta.codigo}`} style={estilos.tarjeta(falta)}>
        <h2 style={estilos.enunciado}>
            <span style={estilos.numero}>{pregunta.numero}</span>
            {pregunta.enunciado}
            {!pregunta.obligatoria && <span style={estilos.opcional}>(opcional)</span>}
        </h2>

        {pregunta.tipo === 'escala' && (
            <Escala pregunta={pregunta} valor={valor} onResponder={onResponder} />
        )}

        {pregunta.tipo === 'opcion' && (
            <div style={estilos.listaOpciones}>
                {pregunta.opciones.map((opcion) => (
                    <BotonOpcion
                        key={opcion.valor}
                        etiqueta={opcion.etiqueta}
                        activo={valor === opcion.valor}
                        onClick={() => onResponder(pregunta.codigo, opcion.valor)}
                    />
                ))}
            </div>
        )}

        {pregunta.tipo === 'texto' && (
            <>
                <textarea
                    style={estilos.textarea}
                    rows={4}
                    maxLength={LARGO_MAXIMO_TEXTO}
                    placeholder={pregunta.placeholder}
                    value={valor || ''}
                    onChange={(e) => onResponder(pregunta.codigo, e.target.value)}
                />
                <div style={estilos.contador}>
                    {(valor || '').length} / {LARGO_MAXIMO_TEXTO}
                </div>
            </>
        )}
    </section>
);

/**
 * Escala de 1 a 5 como botones.
 *
 * Botones y no un slider ni un select: en el celular se responde de un toque y se ve de una cuál
 * está elegida. Los extremos van rotulados debajo, así nadie tiene que adivinar si 1 es lo mejor o
 * lo peor.
 */
const Escala = ({ pregunta, valor, onResponder }) => (
    <>
        <div style={estilos.filaEscala}>
            {[1, 2, 3, 4, 5].map((punto) => (
                <button
                    key={punto}
                    type="button"
                    onClick={() => onResponder(pregunta.codigo, punto)}
                    style={estilos.botonEscala(valor === punto)}
                    aria-pressed={valor === punto}
                >
                    {punto}
                </button>
            ))}
        </div>
        <div style={estilos.extremos}>
            <span>1 · {pregunta.etiquetaMinima}</span>
            <span>5 · {pregunta.etiquetaMaxima}</span>
        </div>
        {pregunta.etiquetaNoAplica && (
            <BotonOpcion
                etiqueta={pregunta.etiquetaNoAplica}
                activo={valor === NO_APLICA}
                onClick={() => onResponder(pregunta.codigo, NO_APLICA)}
            />
        )}
    </>
);

const BotonOpcion = ({ etiqueta, activo, onClick }) => (
    <button type="button" onClick={onClick} style={estilos.botonOpcion(activo)} aria-pressed={activo}>
        <span style={estilos.marca(activo)}>{activo && <i className="bi bi-check" />}</span>
        {etiqueta}
    </button>
);

// Estilos en línea, como el resto de las pantallas públicas (LandingPage, Términos). Mobile-first:
// una sola columna, y los botones con 48px de alto mínimo para que se toquen sin errar.
const estilos = {
    fondo: {
        minHeight: '100vh',
        // Degrade mas marcado que el resto de fondos de página del sistema: esta pantalla la ve
        // todo el personal (llega por WhatsApp) y no hace falta que se mimetice con las pantallas
        // internas de reportes. Misma familia de azul que el resto de la encuesta (header, botones,
        // opciones activas), solo que más saturada.
        background: 'var(--gradient-primary-light)',
        fontFamily: 'var(--font-family-base)',
        color: '#1e293b',
    },
    encabezado: {
        background: COLOR_PRIMARIO,
        color: 'white',
        padding: '2rem 0 1.75rem',
        marginBottom: '1.25rem',
    },
    contenedor: {
        maxWidth: '640px',
        margin: '0 auto',
        padding: '0 1rem',
    },
    titulo: {
        fontSize: 'clamp(1.25rem, 5vw, 1.6rem)',
        fontWeight: 800,
        margin: '0 0 0.6rem',
        lineHeight: 1.3,
    },
    introduccion: {
        fontSize: '0.95rem',
        lineHeight: 1.6,
        margin: 0,
        opacity: 0.92,
    },
    tarjeta: (falta) => ({
        background: 'white',
        borderRadius: '14px',
        padding: '1.1rem 1rem 1.25rem',
        marginBottom: '0.9rem',
        border: falta ? '2px solid #dc2626' : '1px solid #e2e8f0',
        boxShadow: 'var(--shadow-sm)',
    }),
    enunciado: {
        fontSize: '1rem',
        fontWeight: 600,
        lineHeight: 1.45,
        margin: '0 0 0.9rem',
        display: 'flex',
        alignItems: 'baseline',
        flexWrap: 'wrap',
        gap: '0.35rem',
    },
    numero: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: '24px',
        height: '24px',
        borderRadius: '50%',
        background: COLOR_PRIMARIO,
        color: 'white',
        fontSize: '0.8rem',
        fontWeight: 700,
        marginRight: '0.2rem',
        flexShrink: 0,
    },
    opcional: {
        fontSize: '0.8rem',
        fontWeight: 500,
        color: '#94a3b8',
    },
    filaEscala: {
        display: 'grid',
        gridTemplateColumns: 'repeat(5, 1fr)',
        gap: '0.5rem',
    },
    botonEscala: (activo) => ({
        height: '52px',
        borderRadius: '10px',
        border: activo ? `2px solid ${COLOR_PRIMARIO}` : '1px solid #cbd5e1',
        background: activo ? COLOR_PRIMARIO : 'white',
        color: activo ? 'white' : '#334155',
        fontSize: '1.05rem',
        fontWeight: 700,
        cursor: 'pointer',
        transition: 'all 0.15s',
    }),
    extremos: {
        display: 'flex',
        justifyContent: 'space-between',
        fontSize: '0.75rem',
        color: '#64748b',
        margin: '0.45rem 0 0.25rem',
        gap: '0.5rem',
    },
    listaOpciones: {
        display: 'flex',
        flexDirection: 'column',
        gap: '0.5rem',
    },
    botonOpcion: (activo) => ({
        width: '100%',
        minHeight: '48px',
        display: 'flex',
        alignItems: 'center',
        gap: '0.6rem',
        padding: '0.7rem 0.9rem',
        marginTop: '0.5rem',
        borderRadius: '10px',
        border: activo ? `2px solid ${COLOR_PRIMARIO}` : '1px solid #cbd5e1',
        background: activo ? '#eff6ff' : 'white',
        color: activo ? COLOR_PRIMARIO : '#334155',
        fontSize: '0.95rem',
        fontWeight: activo ? 600 : 500,
        textAlign: 'left',
        cursor: 'pointer',
        transition: 'all 0.15s',
    }),
    marca: (activo) => ({
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '22px',
        height: '22px',
        borderRadius: '50%',
        flexShrink: 0,
        border: activo ? `2px solid ${COLOR_PRIMARIO}` : '2px solid #cbd5e1',
        background: activo ? COLOR_PRIMARIO : 'white',
        color: 'white',
        fontSize: '0.8rem',
    }),
    textarea: {
        width: '100%',
        borderRadius: '10px',
        border: '1px solid #cbd5e1',
        padding: '0.75rem',
        fontSize: '0.95rem',
        fontFamily: 'inherit',
        color: '#1e293b',
        resize: 'vertical',
        boxSizing: 'border-box',
    },
    contador: {
        textAlign: 'right',
        fontSize: '0.75rem',
        color: '#94a3b8',
        marginTop: '0.25rem',
    },
    error: {
        background: '#fef2f2',
        border: '1px solid #fecaca',
        color: '#b91c1c',
        borderRadius: '10px',
        padding: '0.8rem 1rem',
        fontSize: '0.9rem',
        marginBottom: '0.9rem',
    },
    botonEnviar: (enviando) => ({
        width: '100%',
        minHeight: '52px',
        borderRadius: '12px',
        border: 'none',
        background: enviando ? '#94a3b8' : COLOR_PRIMARIO,
        color: 'white',
        fontSize: '1rem',
        fontWeight: 700,
        cursor: enviando ? 'default' : 'pointer',
        marginTop: '0.5rem',
    }),
    pieAnonimo: {
        textAlign: 'center',
        fontSize: '0.8rem',
        color: '#64748b',
        marginTop: '1rem',
    },
    tarjetaCierre: {
        background: 'white',
        borderRadius: '16px',
        padding: '2.5rem 1.5rem',
        textAlign: 'center',
        border: '1px solid #e2e8f0',
        boxShadow: 'var(--shadow-md)',
    },
    iconoCierre: {
        width: '64px',
        height: '64px',
        borderRadius: '50%',
        background: COLOR_PRIMARIO,
        color: 'white',
        fontSize: '2rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto 1.25rem',
    },
    tituloCierre: {
        fontSize: '1.05rem',
        fontWeight: 600,
        lineHeight: 1.6,
        color: '#1e293b',
        margin: 0,
    },
};

export default EncuestaPublicaPage;
